import React from 'react';
import { Box, Typography, Row, Col, Image, Accordion } from '@lucid/lego';
import type { Theme, VehicleData } from '../types';
import styles from './AssetOverview.module.scss';

type AssetOverviewProps = {
  /** Vehicle asset data */
  asset: VehicleData;
  /** Theme for the component */
  theme: Theme;
};

/**
 * Asset overview accordion section displaying vehicle details
 * @component
 * @param {AssetOverviewProps} props - Component props
 * @returns {JSX.Element} Accordion with vehicle image and specifications
 * @example
 * <AssetOverview asset={vehicleData} theme="dark" />
 */
const AssetOverview: React.FC<AssetOverviewProps> = ({ asset, theme }) => {
  const renderField = (label: string, value: string) => (
    <Col xl={6} lg={6} key={label}>
      <Typography variant="label" size="small" theme={theme} color="gray-5">
        {label}
      </Typography>
      <Box mt={4}>
        <Typography variant="body" size="small" theme={theme} color="white">
          {value || '-'}
        </Typography>
      </Box>
    </Col>
  );

  return (
    <Accordion title="Asset Overview" theme={theme} defaultOpen={true} showBorder={true}>
      <Box className={styles.assetOverview}>
        {/* Vehicle Header */}
        <Typography variant="h3" size="medium" theme={theme} color="white">
          {asset.vehicle.year} {asset.vehicle.model}
        </Typography>

        <Row gutter={[24, 24]} className={styles.assetContent}>
          {/* Vehicle Image Section */}
          <Col xs={12} md={5}>
            <Box className={styles.vehicleImageContainer}>
              <Image
                uri_mobile={asset.vehicle.image.uri_mobile}
                uri_tablet={asset.vehicle.image.uri_tablet}
                uri_desktop={asset.vehicle.image.uri_desktop}
                alt={asset.vehicle.image.alt}
                theme={theme}
                className={styles.vehicleImage}
                rounded={true}
                useAspectRatio={false}
              />
            </Box>
          </Col>

          {/* Details Section */}
          <Col xs={12} md={7}>
            <Box background="gray-9" theme={theme} className={styles.detailsContent}>
              <Row gutter={[8, 16]}>
                {renderField('VIN', asset.details.vin)}
                {renderField('SW Version', asset.details.swVersion)}
                {renderField('Mileage', asset.details.mileage)}
                {renderField('Colour', asset.details.colour)}
                {renderField('Warranty', asset.details.warranty)}
                {renderField('Battery', asset.details.battery)}
              </Row>
            </Box>
          </Col>
        </Row>
      </Box>
    </Accordion>
  );
};

export default AssetOverview;
